import { createPlayerI18n, phrase } from '../i18n/language.js';
import {
  DEFAULT_LIMIT,
  displayName,
  fetchPlayerTurnovers,
  formatBdt,
  formatTurnoverDate,
  visualProgressPercent,
} from './helpers.js';

export function createTurnoverPage({ useState, useEffect, useCallback, jsx, jsxs }) {
  const usePlayerI18n = createPlayerI18n({ useState, useEffect });
  function ClipboardEmpty() {
    return jsxs('div', {
      className: 'to-empty',
      children: [
        jsxs('svg', {
          className: 'to-empty-icon',
          viewBox: '0 0 64 64',
          'aria-hidden': 'true',
          children: [
            jsx('rect', {
              x: '18',
              y: '12',
              width: '28',
              height: '40',
              rx: '3',
              fill: '#3a3a3a',
              stroke: '#8d8d8d',
              strokeWidth: '2',
            }, 'a'),
            jsx('rect', {
              x: '24',
              y: '8',
              width: '16',
              height: '8',
              rx: '2',
              fill: '#5a5a5a',
              stroke: '#8d8d8d',
              strokeWidth: '2',
            }, 'b'),
            jsx('path', {
              d: 'M26 28h12M26 36h12M26 44h8',
              fill: 'none',
              stroke: '#8d8d8d',
              strokeWidth: '2',
              strokeLinecap: 'round',
            }, 'c'),
          ],
        }),
        jsx('p', { children: phrase('No Data') }),
      ],
    });
  }

  function TurnoverTicket({ row, lang, t }) {
    const pct = visualProgressPercent(row.progressPercent);
    const status = row.status === 'COMPLETED' ? 'COMPLETED' : 'ACTIVE';
    const title = displayName(row);
    const source = row.sourceLabel && row.sourceLabel !== title ? row.sourceLabel : '';
    return jsxs('article', {
      className: `to-ticket to-ticket-${status.toLowerCase()}`,
      'data-turnover-id': row.id,
      children: [
        jsxs('div', {
          className: 'to-ticket-head',
          children: [
            jsx('h3', { className: 'to-ticket-title', children: title }),
            source
              ? jsx('p', { className: 'to-ticket-source', children: source })
              : null,
            jsx('p', {
              className: 'to-ticket-date',
              children: formatTurnoverDate(row.createdAt, lang),
            }),
          ],
        }),
        jsxs('div', {
          className: 'to-ticket-body',
          children: [
            jsxs('div', {
              className: 'to-ticket-row',
              children: [
                jsxs('div', {
                  className: 'to-ticket-amount',
                  children: [
                    jsx('span', { className: 'to-ticket-amount-label', children: t('toRequired') }),
                    jsx('strong', { children: formatBdt(row.requiredTurnover, lang) }),
                  ],
                }),
                jsx('p', {
                  className: `to-ticket-status to-ticket-status-${status.toLowerCase()}`,
                  children: status === 'COMPLETED' ? t('toCompleted') : t('toActive'),
                }),
              ],
            }),
            jsxs('div', {
              className: 'to-ticket-stat',
              children: [
                jsx('span', { children: t('toCompletedTurnover') }),
                jsx('strong', { children: formatBdt(row.completedTurnover, lang) }),
              ],
            }),
            jsxs('div', {
              className: 'to-progress',
              'aria-label': `${pct}%`,
              children: [
                jsx('div', {
                  className: 'to-progress-track',
                  children: jsx('div', {
                    className: 'to-progress-fill',
                    style: { width: `${pct}%` },
                  }),
                }),
                jsx('span', { className: 'to-progress-pct', children: `${pct}%` }),
              ],
            }),
            status === 'COMPLETED' && row.completedAt
              ? jsxs('p', {
                className: 'to-ticket-completed-at',
                children: [t('toCompletedAt'), ': ', formatTurnoverDate(row.completedAt, lang)],
              })
              : null,
          ],
        }),
      ],
    });
  }

  function useTurnoverList(status) {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async (nextPage, append) => {
      setLoading(true);
      setError('');
      try {
        const payload = await fetchPlayerTurnovers({ status, page: nextPage, limit: DEFAULT_LIMIT });
        const list = Array.isArray(payload.data) ? payload.data : [];
        setRows((prev) => (append ? [...prev, ...list] : list));
        setPage(payload.page || nextPage);
        setTotalPages(payload.totalPages || 0);
      } catch (err) {
        if (!append) setRows([]);
        setError(err.message || phrase('Failed to load'));
      } finally {
        setLoading(false);
      }
    }, [status]);

    useEffect(() => {
      load(1, false);
    }, [load]);

    return {
      rows,
      loading,
      error,
      hasMore: page < totalPages,
      loadMore: () => load(page + 1, true),
    };
  }

  function TurnoverPage({ t, lang = 'en', onClose, onBack }) {
    const { phrase } = usePlayerI18n();
    const [tab, setTab] = useState('ACTIVE');
    const list = useTurnoverList(tab);
    const closePage = () => {
      if (typeof onClose === 'function') onClose();
      else if (typeof onBack === 'function') onBack();
    };
    const label = (key) => (typeof t === 'function' ? t(key) : key);

    return jsxs('div', {
      className: 'to-page',
      'aria-label': phrase('Turnover'),
      children: [
        jsxs('header', {
          className: 'to-header',
          children: [
            jsx('h1', { className: 'to-title', children: phrase('Turnover') }),
            jsx('button', {
              type: 'button',
              className: 'to-close',
              'aria-label': phrase('Close'),
              onClick: closePage,
              children: jsx('svg', {
                className: 'to-icon',
                viewBox: '0 0 24 24',
                'aria-hidden': 'true',
                children: jsx('path', {
                  d: 'M6 6l12 12M18 6L6 18',
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: '2',
                  strokeLinecap: 'round',
                }),
              }),
            }),
          ],
        }),
        jsxs('div', {
          className: 'to-tabs',
          role: 'tablist',
          children: [
            jsx('button', {
              type: 'button',
              role: 'tab',
              'aria-selected': tab === 'ACTIVE',
              className: tab === 'ACTIVE' ? 'to-tab is-on' : 'to-tab',
              onClick: () => setTab('ACTIVE'),
              children: label('toActive'),
            }),
            jsx('button', {
              type: 'button',
              role: 'tab',
              'aria-selected': tab === 'COMPLETED',
              className: tab === 'COMPLETED' ? 'to-tab is-on' : 'to-tab',
              onClick: () => setTab('COMPLETED'),
              children: label('toCompleted'),
            }),
          ],
        }),
        list.error
          ? jsx('p', { className: 'to-error', children: list.error })
          : null,
        list.loading && !list.rows.length
          ? jsx('p', { className: 'to-loading account-local-status', children: label('toLoading') })
          : null,
        !list.loading && !list.error && !list.rows.length
          ? jsx(ClipboardEmpty, {})
          : null,
        list.rows.length
          ? jsx('div', {
            className: 'to-list',
            children: list.rows.map((row) => jsx(TurnoverTicket, {
              key: row.id,
              row,
              lang,
              t: label,
            })),
          })
          : null,
        list.hasMore
          ? jsx('button', {
            type: 'button',
            className: 'to-more',
            onClick: list.loadMore,
            disabled: list.loading,
            children: label('toLoadMore'),
          })
          : null,
      ],
    });
  }

  return TurnoverPage;
}
